import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, ShieldStar, User, Trash, Prohibit, CheckCircle, EnvelopeSimple, Copy, Check, X } from "@phosphor-icons/react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useTranslation } from 'react-i18next'

const STATUS_STYLES = {
  active: "bg-emerald-50 text-emerald-700",
  pending: "bg-amber-50 text-amber-700",
  invited: "bg-sky-50 text-sky-700",
  rejected: "bg-red-50 text-red-600",
  suspended: "bg-red-50 text-red-600",
};

export default function Users() {
  const { t } = useTranslation()
  const qc = useQueryClient();
  const { user: me } = useAuth();
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "", role: "team" });
  const [showInvite, setShowInvite] = useState(false);
  const [invite, setInvite] = useState({ name: "", email: "", role: "team" });
  const [inviteLink, setInviteLink] = useState("");
  const [copied, setCopied] = useState(false);

  const { data: users = [], isLoading } = useQuery({ queryKey: ["users"], queryFn: async () => (await api.get("/users")).data });

  const invalidate = () => qc.invalidateQueries({ queryKey: ["users"] });

  const create = useMutation({
    mutationFn: async (payload) => (await api.post("/users", payload)).data,
    onSuccess: () => { invalidate(); setShowForm(false); setForm({ name: "", email: "", phone: "", password: "", role: "team" }); toast.success(t("onboarding.userCreated", "User created")); },
    onError: (e) => toast.error(e.response?.data?.detail || t("onboarding.actionFailed", "Action failed")),
  });
  const update = useMutation({
    mutationFn: async ({ id, payload }) => (await api.put(`/users/${id}`, payload)).data,
    onSuccess: () => { invalidate(); toast.success(t("onboarding.updated", "Updated")); },
    onError: (e) => toast.error(e.response?.data?.detail || t("onboarding.actionFailed", "Action failed")),
  });
  const remove = useMutation({
    mutationFn: async (id) => (await api.delete(`/users/${id}`)).data,
    onSuccess: () => { invalidate(); toast.success(t("onboarding.userDeleted", "User deleted")); },
    onError: (e) => toast.error(e.response?.data?.detail || t("onboarding.actionFailed", "Action failed")),
  });
  const approve = useMutation({
    mutationFn: async ({ id, role }) => (await api.post(`/users/${id}/approve`, { role })).data,
    onSuccess: () => { invalidate(); toast.success(t("onboarding.approved", "User approved")); },
    onError: (e) => toast.error(e.response?.data?.detail || t("onboarding.actionFailed", "Action failed")),
  });
  const reject = useMutation({
    mutationFn: async (id) => (await api.post(`/users/${id}/reject`)).data,
    onSuccess: () => { invalidate(); toast.success(t("onboarding.rejectedToast", "Request rejected")); },
    onError: (e) => toast.error(e.response?.data?.detail || t("onboarding.actionFailed", "Action failed")),
  });
  const sendInvite = useMutation({
    mutationFn: async (payload) => (await api.post("/users/invite", payload)).data,
    onSuccess: (data) => {
      invalidate();
      setInviteLink(data?.activation_link || "");
      setInvite({ name: "", email: "", role: "team" });
      toast.success(t("onboarding.inviteCreated", "Invitation created"));
    },
    onError: (e) => toast.error(e.response?.data?.detail || t("onboarding.actionFailed", "Action failed")),
  });

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(inviteLink); setCopied(true); toast.success(t("onboarding.linkCopied", "Link copied")); setTimeout(() => setCopied(false), 2000); } catch {}
  };

  const pending = users.filter((u) => u.status === "pending");

  const statusLabel = (s) => t(`onboarding.status.${s || "active"}`, s || "active");

  return (
    <div>
      <PageHeader
        title={t('teamUsers', 'Team & Users')}
        subtitle={t('onboarding.manageTeam', 'Manage team members, approvals and permissions')}
        actions={
          <div className="flex gap-2">
            <Button data-testid="invite-user-btn" variant="outline" onClick={() => { setShowInvite(true); setInviteLink(""); }} className="rounded-sm">
              <EnvelopeSimple size={16} className="mr-1.5" /> {t('onboarding.inviteUser', 'Invite User')}
            </Button>
            <Button data-testid="add-user-btn" onClick={() => setShowForm(true)} className="rounded-sm bg-[#0B2545] hover:bg-[#1a365d] text-white">
              <Plus size={16} className="mr-1.5" /> {t('addUser', 'Add User')}
            </Button>
          </div>
        }
      />

      {pending.length > 0 && (
        <div className="mb-6 bg-white border border-amber-200 rounded-sm p-4" data-testid="pending-registrations">
          <div className="text-sm font-semibold text-amber-800 mb-3">
            {t('onboarding.pendingRegistrations', 'Pending registrations')} ({pending.length})
          </div>
          <div className="space-y-2">
            {pending.map((u) => (
              <div key={u.id} data-testid={`pending-row-${u.id}`} className="flex flex-wrap items-center justify-between gap-3 border border-amber-100 rounded-sm px-3 py-2">
                <div className="min-w-0">
                  <div className="font-medium text-slate-900 truncate">{u.name} <span className="text-slate-400 text-xs">· {u.email}</span></div>
                  <div className="text-xs text-slate-500">
                    {[u.company, u.phone].filter(Boolean).join(" · ")}
                    {u.requested_role ? ` · ${t('onboarding.requestedRole', 'Requested role')}: ${u.requested_role}` : ""}
                  </div>
                </div>
                <div className="flex items-center gap-1.5">
                  <Select defaultValue={u.requested_role || "team"} onValueChange={(v) => (u.__role = v)}>
                    <SelectTrigger className="w-28 h-8 rounded-sm bg-white text-xs" data-testid={`approve-role-${u.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white"><SelectItem value="admin">{t('admin', 'Admin')}</SelectItem><SelectItem value="team">{t('team', 'Team')}</SelectItem></SelectContent>
                  </Select>
                  <Button size="sm" className="h-8 rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`approve-${u.id}`}
                    onClick={() => approve.mutate({ id: u.id, role: u.__role || u.requested_role || "team" })}>
                    <Check size={14} className="mr-1" />{t('onboarding.approve', 'Approve')}
                  </Button>
                  <Button size="sm" variant="outline" className="h-8 rounded-sm text-red-600 border-red-200" data-testid={`reject-${u.id}`}
                    onClick={() => reject.mutate(u.id)}>
                    <X size={14} className="mr-1" />{t('onboarding.reject', 'Reject')}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="flex justify-center py-20"><div className="animate-spin h-8 w-8 border-2 border-[#0B2545] border-t-transparent rounded-full" /></div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
              <tr>
                <th className="text-left px-4 py-3">{t("name", "Name")}</th>
                <th className="text-left px-4 py-3 hidden sm:table-cell">{t('email', 'Email')}</th>
                <th className="text-left px-4 py-3 hidden md:table-cell">{t('onboarding.phone', 'Phone')}</th>
                <th className="text-left px-4 py-3">{t("role", "Role")}</th>
                <th className="text-left px-4 py-3">{t('status', 'Status')}</th>
                <th className="text-right px-4 py-3">{t('actions', 'Actions')}</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} data-testid={`user-row-${u.id}`} className="border-t border-slate-100">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className="w-8 h-8 rounded-sm bg-[#0B2545] text-white flex items-center justify-center text-xs">{u.name?.charAt(0)}</span>
                      <span className="font-medium text-slate-900">{u.name}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 hidden sm:table-cell text-slate-500">{u.email}</td>
                  <td className="px-4 py-3 hidden md:table-cell text-slate-500 whitespace-nowrap">{u.phone || "—"}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-sm ${u.role === "admin" ? "bg-[#C5A059]/15 text-[#8a6d2f]" : "bg-slate-100 text-slate-600"}`}>
                      {u.role === "admin" ? <ShieldStar size={12} /> : <User size={12} />} {u.role}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded-sm ${STATUS_STYLES[u.status] || STATUS_STYLES.active}`} data-testid={`status-${u.id}`}>{statusLabel(u.status)}</span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <Select value={u.role} onValueChange={(v) => update.mutate({ id: u.id, payload: { role: v } })}>
                        <SelectTrigger className="w-24 h-8 rounded-sm bg-white text-xs" data-testid={`role-${u.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-white"><SelectItem value="admin">{t('admin', 'Admin')}</SelectItem><SelectItem value="team">{t('team', 'Team')}</SelectItem></SelectContent>
                      </Select>
                      <button
                        onClick={() => update.mutate({ id: u.id, payload: { status: u.status === "suspended" ? "active" : "suspended" } })}
                        data-testid={`suspend-${u.id}`}
                        className="text-slate-400 hover:text-amber-600 p-1.5" title={t('suspendActivate', 'Suspend / activate')}>
                        {u.status === "suspended" ? <CheckCircle size={16} /> : <Prohibit size={16} />}
                      </button>
                      {u.id !== me?.id && (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <button className="text-slate-400 hover:text-red-600 p-1.5" data-testid={`delete-user-${u.id}`}><Trash size={16} /></button>
                          </AlertDialogTrigger>
                          <AlertDialogContent className="bg-white rounded-sm">
                            <AlertDialogHeader><AlertDialogTitle>{t('deleteName', 'Delete {{name}}?', { name: u.name })}</AlertDialogTitle></AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel className="rounded-sm">{t('cancel', 'Cancel')}</AlertDialogCancel>
                              <AlertDialogAction onClick={() => remove.mutate(u.id)} className="rounded-sm bg-red-600 hover:bg-red-700">{t('delete', 'Delete')}</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add user (direct) */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-md bg-white rounded-sm">
          <DialogHeader><DialogTitle className="font-serif text-2xl">{t('addTeamMember', 'Add Team Member')}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>{t("name", "Name")}</Label><Input data-testid="user-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 rounded-sm" /></div>
            <div><Label>{t('email', 'Email')}</Label><Input data-testid="user-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1 rounded-sm" /></div>
              <div><Label>{t('onboarding.phone', 'Phone')} *</Label><Input data-testid="user-phone" type="tel" required autoComplete="tel" placeholder="+351 ..." value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1 rounded-sm" /></div>
            <div><Label>{t('password', 'Password')}</Label><Input data-testid="user-password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1 rounded-sm" /></div>
            <div>
              <Label>{t("role", "Role")}</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger className="mt-1 rounded-sm bg-white"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white"><SelectItem value="admin">{t('admin', 'Admin')}</SelectItem><SelectItem value="team">{t('teamMember', 'Team member')}</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setShowForm(false)}>{t('cancel', 'Cancel')}</Button>
            <Button data-testid="user-save" disabled={!form.name || !form.email || !form.phone.trim() || !form.password} onClick={() => create.mutate(form)} className="rounded-sm bg-[#0B2545] hover:bg-[#1a365d] text-white">{t('create', 'Create')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invite (activation link) */}
      <Dialog open={showInvite} onOpenChange={(o) => { setShowInvite(o); if (!o) setInviteLink(""); }}>
        <DialogContent className="max-w-md bg-white rounded-sm">
          <DialogHeader><DialogTitle className="font-serif text-2xl">{t('onboarding.inviteTitle', 'Invite a team member')}</DialogTitle></DialogHeader>
          {!inviteLink ? (
            <div className="space-y-4">
              <div><Label>{t("name", "Name")}</Label><Input data-testid="invite-name" value={invite.name} onChange={(e) => setInvite({ ...invite, name: e.target.value })} className="mt-1 rounded-sm" /></div>
              <div><Label>{t('email', 'Email')}</Label><Input data-testid="invite-email" type="email" value={invite.email} onChange={(e) => setInvite({ ...invite, email: e.target.value })} className="mt-1 rounded-sm" /></div>
              <div>
                <Label>{t("role", "Role")}</Label>
                <Select value={invite.role} onValueChange={(v) => setInvite({ ...invite, role: v })}>
                  <SelectTrigger className="mt-1 rounded-sm bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white"><SelectItem value="admin">{t('admin', 'Admin')}</SelectItem><SelectItem value="team">{t('teamMember', 'Team member')}</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-slate-600">{t('onboarding.inviteLinkHelp', 'Invitation created. Share this activation link with the invitee (also emailed if email delivery is configured):')}</p>
              <div className="flex items-center gap-2">
                <Input readOnly value={inviteLink} data-testid="invite-activation-link" className="rounded-sm text-xs" />
                <Button variant="outline" className="rounded-sm shrink-0" onClick={copyLink} data-testid="copy-invite-link">
                  {copied ? <Check size={16} /> : <Copy size={16} />}
                </Button>
              </div>
            </div>
          )}
          <DialogFooter>
            {!inviteLink ? (
              <>
                <Button variant="outline" className="rounded-sm" onClick={() => setShowInvite(false)}>{t('cancel', 'Cancel')}</Button>
                <Button data-testid="invite-save" disabled={!invite.name || !invite.email || sendInvite.isPending} onClick={() => sendInvite.mutate(invite)} className="rounded-sm bg-[#0B2545] hover:bg-[#1a365d] text-white">{t('onboarding.inviteSend', 'Send invitation')}</Button>
              </>
            ) : (
              <Button className="rounded-sm bg-[#0B2545] hover:bg-[#1a365d] text-white" onClick={() => setShowInvite(false)} data-testid="invite-done">{t('done', 'Done')}</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
